import axios, { type AxiosInstance } from 'axios';

/**
 * Shared API client.
 *
 * Notes:
 *   - withCredentials: true so the session cookie travels.
 *   - Reads csrf_token cookie and echoes via X-CSRF-Token header on mutation verbs
 *     (double-submit pattern matching the api's SessionGuard).
 *   - 401 from any request → redirect to /login (session expired).
 */

function readCookie(name: string): string | undefined {
  const all = document.cookie.split(';');
  for (const raw of all) {
    const [k, v] = raw.trim().split('=');
    if (k === name) return decodeURIComponent(v ?? '');
  }
  return undefined;
}

export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || '',
  withCredentials: true,
  timeout: 30_000,
});

api.interceptors.request.use((config) => {
  const method = (config.method ?? 'get').toLowerCase();
  if (method !== 'get' && method !== 'head') {
    const csrf = readCookie('csrf_token');
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err: unknown) => {
    const e = err as { response?: { status?: number; config?: { url?: string } } };
    if (e.response?.status === 401) {
      const url = e.response?.config?.url ?? '';
      // Don't bounce-loop on /me — the AuthProvider handles that pathway.
      if (!url.includes('/api/v1/me') && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login?reason=expired';
      }
    }
    return Promise.reject(err);
  },
);

export interface Me {
  id: string;
  email: string;
  upn: string | null;
  authProvider: 'entra' | 'local';
  role:
    | 'super_admin'
    | 'admin'
    | 'fm'
    | 'supervisor'
    | 'cleaner'
    | 'pantry_worker'
    | 'employee'
    | 'client'
    | 'tenant';
  displayName: string;
  phone: string | null;
  preferredLang: string;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  permissions: Record<string, string[]>;
}
